import React, { useMemo } from 'react';
import { Squad } from '../../../types';

type Props = {
  squads: Squad[];
};

type MonthKey = string; // 'YYYY-MM'

const dayIndexFromCode = (code?: string): number | null => {
  if (!code) return null;
  const idx = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].indexOf(code);
  return idx >= 0 ? idx : null;
};

const getUTCDate = (dateString: string) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const monthKey = (d: Date): MonthKey => {
  const y = d.getUTCFullYear();
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  return `${y}-${m}`;
};

const computeSessionsByMonth = (squad: Squad): Map<MonthKey, number> => {
  const result = new Map<MonthKey, number>();
  const mainDate = squad?.schedule?.mainDate;
  if (mainDate) {
    result.set(monthKey(getUTCDate(mainDate)), 1);
    return result;
  }
  const sd = squad?.schedule?.startDate;
  const ed = squad?.schedule?.endDate;
  const dow = dayIndexFromCode(squad?.schedule?.dayOfWeek);
  if (!sd || !ed || dow === null) return result;

  const start = getUTCDate(sd);
  const end = getUTCDate(ed);
  const exceptionTimes = new Set<number>(
    [...(squad?.schedule?.exceptions || []), ...(squad?.schedule?.inheritedExceptions || [])]
      .filter(Boolean).map(d => getUTCDate(d).getTime())
  );

  // Count regular sessions
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    if (cursor.getUTCDay() === dow) {
      const t = cursor.getTime();
      if (!exceptionTimes.has(t)) {
        const key = monthKey(cursor);
        result.set(key, (result.get(key) || 0) + 1);
      }
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  // Add makeups (not exceptions)
  (squad?.schedule?.makeups || []).filter(Boolean).forEach(d => {
    const dt = getUTCDate(d);
    const t = dt.getTime();
    if (!exceptionTimes.has(t)) {
      const key = monthKey(dt);
      result.set(key, (result.get(key) || 0) + 1);
    }
  });

  return result;
};

const FinancialsTab: React.FC<Props> = ({ squads }) => {
  const {
    monthlyTotals,
    allMonthsSorted,
    perSquadSummaries,
    grandTotal,
    monthlyStudentSeats,
    seatsGrandTotal,
    monthlyClassCounts,
    classesGrandTotal,
  } = useMemo(() => {
    const monthlyTotals = new Map<MonthKey, number>();
    const monthlyStudentSeats = new Map<MonthKey, number>(); // seats (not unique) active in a month
    const monthlyClassCounts = new Map<MonthKey, number>(); // total class sessions per month across squads
    const perSquadSummaries: Array<{
      id: string;
      title: string | undefined;
      studentCount: number;
      pricePerStudent: number;
      totalRevenue: number;
      startDate?: string;
      endDate?: string;
      sessionsTotal: number;
      currency?: string;
    }> = [];

    squads.forEach(sq => {
      const studentCount = (sq.studentIds || []).length;
      const pricePerStudent = (sq.priceCents || 0) / 100;
      const totalRevenue = studentCount * pricePerStudent;

      const sessionsByMonth = computeSessionsByMonth(sq);
      const sessionsTotal = Array.from(sessionsByMonth.values()).reduce((a, b) => a + b, 0);

      // Track seats per month whenever the squad has at least one session in that month
      sessionsByMonth.forEach((count, mKey) => {
        if (count > 0) {
          monthlyStudentSeats.set(mKey, (monthlyStudentSeats.get(mKey) || 0) + studentCount);
        }
        monthlyClassCounts.set(mKey, (monthlyClassCounts.get(mKey) || 0) + count);
      });

      // If there are sessions, allocate proportionally by sessions that month
      if (sessionsTotal > 0) {
        sessionsByMonth.forEach((count, mKey) => {
          const portion = (totalRevenue * count) / sessionsTotal;
          monthlyTotals.set(mKey, (monthlyTotals.get(mKey) || 0) + portion);
        });
      } else {
        // Fallback: allocate entire revenue to the start month if dates exist
        const sd = sq?.schedule?.mainDate || sq?.schedule?.startDate;
        if (sd) {
          const mk = monthKey(getUTCDate(sd));
          monthlyTotals.set(mk, (monthlyTotals.get(mk) || 0) + totalRevenue);
          monthlyStudentSeats.set(mk, (monthlyStudentSeats.get(mk) || 0) + studentCount);
          // No class sessions counted for fallback; class count remains 0 unless sessions exist
        }
      }

      perSquadSummaries.push({
        id: sq.id,
        title: sq.title,
        studentCount,
        pricePerStudent,
        totalRevenue,
        startDate: sq?.schedule?.mainDate || sq?.schedule?.startDate,
        endDate: sq?.schedule?.mainDate || sq?.schedule?.endDate,
        sessionsTotal,
        currency: sq.currency,
      });
    });

    const allMonthsSorted = Array.from(monthlyTotals.keys()).sort();
    const grandTotal = Array.from(monthlyTotals.values()).reduce((a, b) => a + b, 0);
    const seatsGrandTotal = Array.from(monthlyStudentSeats.values()).reduce((a, b) => a + b, 0);
    const classesGrandTotal = Array.from(monthlyClassCounts.values()).reduce((a, b) => a + b, 0);

    return { monthlyTotals, allMonthsSorted, perSquadSummaries, grandTotal, monthlyStudentSeats, seatsGrandTotal, monthlyClassCounts, classesGrandTotal };
  }, [squads]);

  const formatUSD = (n: number) =>
    `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div
      className="financials-tab-container"
      style={{
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        padding: '0 12px',
      }}
    >
      <div
        className="admin-content-card"
        style={{
          width: '100%',
          display: 'block',
        }}
      >
        <h3 style={{ marginTop: 0 }}>💵 Financials Overview</h3>
        <p style={{ color: '#6b7280', margin: 0, lineHeight: 1.6, marginBottom: 12 }}>
          Estimates allocate each squad’s total revenue (students × price) across calendar months,
          proportional to scheduled class days in each month.
        </p>
        <div className="table-wrapper" style={{ width: '100%', overflowX: 'auto' }}>
          <table className="reservations-table" style={{ width: '100%', minWidth: 1000 }}>
            <thead>
              <tr>
                <th className="sticky-col" style={{ minWidth: 200, whiteSpace: 'normal' }}>Metric</th>
                {allMonthsSorted.length === 0 ? null : allMonthsSorted.map(m => <th key={m}>{m}</th>)}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="sticky-col" style={{ minWidth: 200, whiteSpace: 'normal', fontWeight: 600 }}>
                  Projected Revenue
                </td>
                {allMonthsSorted.length === 0 ? (
                  <td colSpan={2} style={{ textAlign: 'center', color: '#6b7280' }}>
                    No scheduled squads with dates found.
                  </td>
                ) : (
                  allMonthsSorted.map(m => (
                    <td key={m} style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {formatUSD(monthlyTotals.get(m) || 0)}
                    </td>
                  ))
                )}
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>{formatUSD(grandTotal)}</td>
              </tr>
              <tr>
                <td className="sticky-col" style={{ minWidth: 200, whiteSpace: 'normal', fontWeight: 600 }}>
                  # Students
                </td>
                {allMonthsSorted.map(m => (
                  <td key={m} style={{ textAlign: 'right' }}>
                    {(monthlyStudentSeats.get(m) || 0).toLocaleString()}
                  </td>
                ))}
                <td style={{ textAlign: 'right' }}>{seatsGrandTotal.toLocaleString()}</td>
              </tr>
              <tr>
                <td className="sticky-col" style={{ minWidth: 200, whiteSpace: 'normal', fontWeight: 600 }}>
                  Revenue / Student
                </td>
                {allMonthsSorted.map(m => {
                  const seats = monthlyStudentSeats.get(m) || 0;
                  const val = seats > 0 ? (monthlyTotals.get(m) || 0) / seats : 0;
                  return (
                    <td key={m} style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {formatUSD(val)}
                    </td>
                  );
                })}
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {seatsGrandTotal > 0 ? formatUSD(grandTotal / seatsGrandTotal) : formatUSD(0)}
                </td>
              </tr>
              <tr>
                <td className="sticky-col" style={{ minWidth: 200, whiteSpace: 'normal', fontWeight: 600 }}>
                  # Classes
                </td>
                {allMonthsSorted.map(m => (
                  <td key={m} style={{ textAlign: 'right' }}>
                    {(monthlyClassCounts.get(m) || 0).toLocaleString()}
                  </td>
                ))}
                <td style={{ textAlign: 'right' }}>{classesGrandTotal.toLocaleString()}</td>
              </tr>
              <tr>
                <td className="sticky-col" style={{ minWidth: 200, whiteSpace: 'normal', fontWeight: 600 }}>
                  Revenue / Class
                </td>
                {allMonthsSorted.map(m => {
                  const classes = monthlyClassCounts.get(m) || 0;
                  const val = classes > 0 ? (monthlyTotals.get(m) || 0) / classes : 0;
                  return (
                    <td key={m} style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {formatUSD(val)}
                    </td>
                  );
                })}
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {classesGrandTotal > 0 ? formatUSD(grandTotal / classesGrandTotal) : formatUSD(0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        className="admin-content-card"
        style={{ marginTop: '1.5rem', width: '100%', display: 'block' }}
      >
        <h3 style={{ marginTop: 0 }}>📋 Per Squad Summary</h3>
        <div className="table-wrapper" style={{ width: '100%' }}>
          <table className="reservations-table">
            <thead>
              <tr>
                <th>Squad</th>
                <th>Students</th>
                <th>Price/Student</th>
                <th>Total Revenue</th>
                <th>Dates</th>
                <th># Sessions</th>
              </tr>
            </thead>
            <tbody>
              {perSquadSummaries.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>
                    No squads available.
                  </td>
                </tr>
              ) : (
                perSquadSummaries
                  .slice()
                  .sort((a, b) => {
                    const taStart = a.startDate ? getUTCDate(a.startDate).getTime() : Number.NEGATIVE_INFINITY;
                    const taEnd = a.endDate ? getUTCDate(a.endDate).getTime() : Number.NEGATIVE_INFINITY;
                    const tbStart = b.startDate ? getUTCDate(b.startDate).getTime() : Number.NEGATIVE_INFINITY;
                    const tbEnd = b.endDate ? getUTCDate(b.endDate).getTime() : Number.NEGATIVE_INFINITY;
                    const ta = Math.max(taStart, taEnd);
                    const tb = Math.max(tbStart, tbEnd);
                    if (tb !== ta) return tb - ta; // newest (largest) first
                    return (a.title || '').localeCompare(b.title || '');
                  })
                  .map(s => (
                    <tr key={s.id}>
                      <td>{s.title || 'Untitled Squad'}</td>
                      <td>{s.studentCount}</td>
                      <td>{formatUSD(s.pricePerStudent)}</td>
                      <td>{formatUSD(s.totalRevenue)}</td>
                      <td>
                        {s.startDate && s.endDate ? `${s.startDate} → ${s.endDate}` : 'N/A'}
                      </td>
                      <td>{s.sessionsTotal}</td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinancialsTab;


